/**
 * The home screen's search and filter (Task 4). Her words: *"Add a search
 * feature to the navigation pane. Users should be able to search by building
 * name, due date, bid date, and survey status. Display a filter icon (funnel)
 * right above the first card. Clicking it opens a list of available
 * filters."*
 *
 * Two pieces, one file, because neither means anything without the other:
 *
 *   `FunnelButton` — the trigger, icon-only since Task 3 (Decision #6): its
 *   name lives in the `aria-label`, not in visible text, so `button.btn`
 *   still reads as "Filter surveys" to a screen reader while fitting beside
 *   the "+" on the "Your surveys" line. It is still caught by
 *   `layout-widths.spec.ts`'s 44px loop like every other control. Per
 *   Decision #10 it renders only once the device holds 2+ surveys —
 *   `HomeScreen` decides that and passes `eligible`, this component does not
 *   re-derive it.
 *
 *   `FilterSheet` — built on the SAME overlay `MapSheet.tsx` uses: `.veil` /
 *   `.veil--on`, `.sheet` / `.sheet--open`, `.sheet__head`, `.sheet__title`,
 *   `.sheet__sub`, `.sheet__close`. This is the app's second sheet, not a
 *   second overlay system — it sits in the same stacking band (theme.css,
 *   "topbar 30 · mapbar 30 · veil 40 · sheet 50 · toast 60") and reuses those
 *   exact z-indexes rather than inventing new ones.
 *
 * PRESENTATIONAL throughout: a `SurveyFilter` value in, an `onChange` out.
 * The arithmetic — what counts as active, what a row has to match — lives in
 * `src/ui/state/surveyFilter.ts`, pure and unaware this component exists.
 */

import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';

import { EMPTY_FILTER, filterCount, type SurveyFilter } from '../state/surveyFilter';
import type { TagKind } from '../state/surveyList';

const STATUS_OPTIONS: ReadonlyArray<{ kind: TagKind; label: string }> = [
  { kind: 'assigned', label: 'Assigned' },
  { kind: 'field', label: 'In progress' },
  { kind: 'submitted', label: 'Submitted' },
];

/** Hand-drawn — Constraint 2, no icon library. A funnel: a wide mouth to a stem. */
function FunnelIcon(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
      <path
        d="M3 4.5h18l-6.75 8.1v5.4l-4.5 2.4v-7.8z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

export interface FunnelButtonProps {
  filter: SurveyFilter;
  onOpen: () => void;
  buttonRef: React.RefObject<HTMLButtonElement | null>;
}

/** The trigger — a 44px icon button, twin to the "+" (`HomeScreen` places both in the head row). */
export function FunnelButton({ filter, onOpen, buttonRef }: FunnelButtonProps): ReactNode {
  const count = filterCount(filter);
  return (
    <button
      type="button"
      className="btn btn--funnel"
      ref={buttonRef}
      onClick={onOpen}
      aria-label={count > 0 ? `Filter surveys — ${String(count)} active` : 'Filter surveys'}
    >
      <FunnelIcon />
      {/* The "Filter" word is gone (Decision #6) — fitting `YOUR SURVEYS` and
       * two controls into 398px left no room for it — but the aria-label
       * above still names the button, so nothing a screen reader or a test
       * can find by name is lost. */}
      {count > 0 ? (
        <span className="badge badge--funnel" data-legibility="display">
          {count}
        </span>
      ) : null}
    </button>
  );
}

export interface FilterSheetProps {
  filter: SurveyFilter;
  onChange: (next: SurveyFilter) => void;
  open: boolean;
  onClose: () => void;
  /** Where focus goes back to on close — the funnel button. */
  returnFocusTo: React.RefObject<HTMLElement | null>;
}

/** The list of available filters (her words) — text, status, two date ranges. */
export function FilterSheet({ filter, onChange, open, onClose, returnFocusTo }: FilterSheetProps): ReactNode {
  const closeButton = useRef<HTMLButtonElement | null>(null);
  const textId = useId();
  const dueFromId = useId();
  const dueToId = useId();
  const bidFromId = useId();
  const bidToId = useId();

  // Round 5, Decision #11 — her words: *"When you click the Filter Surveys
  // button and start typing the building name, the keyboard minimizes after
  // every letter, making it impossible to continuously type a full building
  // name."* `onClose` and `returnFocusTo` are held in refs, updated on every
  // render, so the effect below can read current values without listing them
  // as deps. The text field's value lives in `HomeScreen`'s filter state, so
  // every keystroke re-renders this component; if `onClose` were a dep (and
  // `SurveyFilterBar` passes a fresh arrow function each render), that
  // re-render would run this effect's cleanup — which calls
  // `returnFocusTo.current?.focus()` — and then the effect body again, which
  // calls `closeButton.current?.focus()`. Focus would leave the input twice
  // per letter, and iOS drops the keyboard the moment focus is no longer on
  // an input. Depending on `[open]` alone means the effect only runs when the
  // sheet opens or closes, never on every keystroke.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const returnFocusToRef = useRef(returnFocusTo);
  returnFocusToRef.current = returnFocusTo;

  useEffect(() => {
    if (!open) return undefined;
    closeButton.current?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      returnFocusToRef.current.current?.focus();
    };
  }, [open]);

  const count = filterCount(filter);
  const chosenStatus = new Set(filter.status);

  const toggleStatus = (kind: TagKind): void => {
    onChange({
      ...filter,
      status: chosenStatus.has(kind)
        ? filter.status.filter((k) => k !== kind)
        : [...filter.status, kind],
    });
  };

  const clear = (): void => onChange(EMPTY_FILTER);

  return (
    <>
      <div className={open ? 'veil veil--on' : 'veil'} onClick={onClose} aria-hidden="true" />
      <div
        className={open ? 'sheet sheet--open' : 'sheet'}
        role="dialog"
        aria-modal="true"
        aria-label="Filter surveys"
        aria-hidden={!open}
      >
        <div className="sheet__head">
          <span className="sheet__grab" aria-hidden="true" />
          <div className="sheet__title" data-legibility="display">
            Filter surveys
            <small className="sheet__sub" data-legibility="display">
              {count > 0 ? `${String(count)} active` : 'No filters active'}
            </small>
          </div>
          <button
            type="button"
            className="sheet__close"
            aria-label="Close filters"
            onClick={onClose}
            ref={closeButton}
            tabIndex={open ? 0 : -1}
          >
            ✕
          </button>
        </div>

        <div className="filtersheet__body">
          <div className="filtersheet__field">
            <label className="filtersheet__label" data-legibility="question" htmlFor={textId}>
              Building name
            </label>
            <input
              id={textId}
              className="filtersheet__input"
              type="search"
              value={filter.text}
              placeholder="Search by building name"
              tabIndex={open ? 0 : -1}
              onChange={(event) => onChange({ ...filter, text: event.target.value })}
            />
          </div>

          <div className="filtersheet__field">
            <span className="filtersheet__label" data-legibility="question">
              Status
            </span>
            <div className="filtersheet__status" role="group" aria-label="Status">
              {STATUS_OPTIONS.map((option) => (
                <button
                  key={option.kind}
                  type="button"
                  className="filtersheet__opt"
                  aria-pressed={chosenStatus.has(option.kind)}
                  data-legibility="body"
                  tabIndex={open ? 0 : -1}
                  onClick={() => toggleStatus(option.kind)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="filtersheet__field">
            <label className="filtersheet__label" data-legibility="question" htmlFor={dueFromId}>
              Due from
            </label>
            <input
              id={dueFromId}
              className="filtersheet__input filtersheet__input--mono"
              type="date"
              value={filter.dueFrom ?? ''}
              tabIndex={open ? 0 : -1}
              onChange={(event) =>
                onChange({ ...filter, dueFrom: event.target.value === '' ? undefined : event.target.value })
              }
            />
          </div>

          <div className="filtersheet__field">
            <label className="filtersheet__label" data-legibility="question" htmlFor={dueToId}>
              Due to
            </label>
            <input
              id={dueToId}
              className="filtersheet__input filtersheet__input--mono"
              type="date"
              value={filter.dueTo ?? ''}
              tabIndex={open ? 0 : -1}
              onChange={(event) =>
                onChange({ ...filter, dueTo: event.target.value === '' ? undefined : event.target.value })
              }
            />
          </div>

          <div className="filtersheet__field">
            <label className="filtersheet__label" data-legibility="question" htmlFor={bidFromId}>
              Bid due from
            </label>
            <input
              id={bidFromId}
              className="filtersheet__input filtersheet__input--mono"
              type="date"
              value={filter.bidFrom ?? ''}
              tabIndex={open ? 0 : -1}
              onChange={(event) =>
                onChange({ ...filter, bidFrom: event.target.value === '' ? undefined : event.target.value })
              }
            />
          </div>

          <div className="filtersheet__field">
            <label className="filtersheet__label" data-legibility="question" htmlFor={bidToId}>
              Bid due to
            </label>
            <input
              id={bidToId}
              className="filtersheet__input filtersheet__input--mono"
              type="date"
              value={filter.bidTo ?? ''}
              tabIndex={open ? 0 : -1}
              onChange={(event) =>
                onChange({ ...filter, bidTo: event.target.value === '' ? undefined : event.target.value })
              }
            />
          </div>

          <button
            type="button"
            className="filtersheet__clear"
            disabled={count === 0}
            data-legibility="body"
            tabIndex={open ? 0 : -1}
            onClick={clear}
          >
            Clear
          </button>
        </div>
      </div>
    </>
  );
}

export interface SurveyFilterBarProps {
  filter: SurveyFilter;
  onChange: (next: SurveyFilter) => void;
  /** Decision #10: renders only once the device holds 2+ surveys. */
  eligible: boolean;
}

/** The funnel and its sheet, wired together — what `HomeScreen` renders. */
export function SurveyFilterBar({ filter, onChange, eligible }: SurveyFilterBarProps): ReactNode {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  // The ref fix in `FilterSheet` is the guarantee against focus-stealing on
  // every keystroke; this `useCallback` is the courtesy on top — it keeps
  // this prop referentially stable so the child does not receive a new
  // function on every render in the first place.
  const closeSheet = useCallback(() => setOpen(false), []);

  if (!eligible) return null;

  return (
    <>
      <FunnelButton filter={filter} onOpen={() => setOpen(true)} buttonRef={buttonRef} />
      <FilterSheet
        filter={filter}
        onChange={onChange}
        open={open}
        onClose={closeSheet}
        returnFocusTo={buttonRef}
      />
    </>
  );
}
